/* eslint-disable no-shadow */
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { detect } from "detect-browser";
import { formatRelative } from "date-fns";
import { enGB } from "date-fns/locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faUsers,
  faFileAlt,
  faTimesCircle,
  faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";
import engagementType from "./types/engagementType";
import {
  Wrapper,
  Column,
  Top,
  Badge,
  BadgeTooltip,
  Bottom,
  BottomItem,
  Divider,
  MobileDivider,
  Number,
  Link,
  EmployerName,
  SelectButton,
  CvLimitFull,
  CvLimitFullText,
  NumberOfPositions,
  NumberOfPositionsPlaceholder,
  JobTitle,
} from "./styles/engagement-styles";
import trackEvent from "../shared/ga/trackEvent";
import TextLink from "../library/TextLink";
import { percentageFeeTooltip, exclusiveJobTooltip } from "../../constants";
import { isFeatureEnabled } from "../../helpers";

export const Engagement = ({
  candidateId,
  engagement,
  engagementCategoryFilter,
  selectingCandidateActive,
  style,
  isPortalUser,
  isPartnerUser,
  onShowInfoClick,
  onDisengageClick,
  onAcceptClick,
}) => {
  const isIE = detect().name === "ie";

  const isContract = engagement.job.jobType === "contract";
  // because the salaryRange value for a contract job
  // already contains a currency symbol - see
  // app/decorators/job_decorator.rb#salary_abbreviated
  const currencySymbol = isContract ? null : engagement.job.currency.symbol;

  const isInfiniteCvLimit = engagement.job.cvLimit === 0;

  const handleLink = (link) => {
    window.location.assign(link);
  };

  const handleTitleLength = (title) => {
    if (title.length > 34 && window.innerWidth < 380) {
      return `${title.slice(0, 34)}...`;
    }
    return title;
  };

  const handleTitle = (jobTitle, jobOnHold) => {
    let title = handleTitleLength(jobTitle);

    if (jobOnHold) {
      title = title.concat(" (on hold)");
    }

    return title;
  };

  const cvLimitReached =
    engagement.applicationsCount > engagement.job.cvLimit && !isInfiniteCvLimit;
  const cvLimitToolTip = (cvLimitReached) => {
    if (cvLimitReached) {
      return `CV limit<br>reached`;
    }
    return `CV<br>limit`;
  };

  const multiplePostionsTooltip = (acceptedOffers, positionsCount) => `
      <strong>${acceptedOffers} offers</strong> have been accepted out<br>
      of the <strong>${positionsCount} positions</strong>
    `;

  const appsToolTip = `
      <strong>Applications:</strong><br>
      ${engagement.userApplicationCountUnread} unread<br>
      ${engagement.userApplicationCountRead} read
    `;

  const noLinkStatuses = [
    "rejected",
    "disengaged",
    "closed",
    "position_filled",
  ];

  const noEmployerNameStatuses = ["pending", "rejected", "disengaged"];

  const jobTitleLink = (engagementStatus, jobStatus) => {
    const statuses = [engagementStatus, jobStatus];
    let link;

    if (statuses.some((state) => noLinkStatuses.indexOf(state) >= 0)) {
      link = handleTitleLength(engagement.job.title);
    } else if (statuses.includes("pending")) {
      return (
        /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
        <Link
          id="job-status-link"
          title="View Job Profile"
          underline={engagement.jobProfileLink}
          onClick={onShowInfoClick}
        >
          {handleTitle(engagement.job.title, engagement.job.onHold)}
        </Link>
      );
    } else {
      link = (
        <Link
          href={engagement.jobProfileLink}
          title="View Job Profile"
          underline={engagement.jobProfileLink}
        >
          {handleTitle(engagement.job.title, engagement.job.onHold)}
        </Link>
      );
    }

    return link;
  };

  const employerName = (engagementStatus, jobStatus) => {
    const statuses = [engagementStatus, jobStatus];
    let name;

    if (noEmployerNameStatuses.includes(engagementStatus)) {
      name = "";
    } else if (statuses.some((state) => noLinkStatuses.indexOf(state) >= 0)) {
      name = <EmployerName>{engagement.job.employer.name}</EmployerName>;
    } else {
      name = (
        <EmployerName>
          <Link
            href={engagement.employerProfileLink}
            employerName
            title="View Employer Profile"
          >
            {engagement.job.employer.name}
          </Link>
        </EmployerName>
      );
    }

    return name;
  };

  const displayAgencyFee = (engagementCategoryFilter, isPortalUser) => {
    let fee;

    if (engagementCategoryFilter === "your_filled") {
      fee = (
        <Fragment>
          <BottomItem data-test-id="blank-fee" />
        </Fragment>
      );
    } else {
      fee = (
        <Fragment>
          {isPortalUser ? (
            <BottomItem />
          ) : (
            <BottomItem data-test-id="fee-percent-format">
              {engagement.job.placementFeePercent}%
            </BottomItem>
          )}
        </Fragment>
      );
    }
    return fee;
  };

  const displayAgencyFeeMobile = (engagementCategoryFilter, isPortalUser) => {
    let fee;

    if (engagementCategoryFilter === "your_filled") {
      fee = (
        <BottomItem noDesktop>
          <strong>{currencySymbol}</strong> {engagement.job.salaryRange}
        </BottomItem>
      );
    } else {
      fee = (
        <BottomItem noDesktop>
          <strong>{currencySymbol}</strong> {engagement.job.salaryRange}
          {!isPortalUser && (
            <Fragment>
              <MobileDivider>|</MobileDivider>{" "}
              {engagement.job.placementFeePercent}%
            </Fragment>
          )}
        </BottomItem>
      );
    }

    return fee;
  };

  const displayedStatus = (engagementStatus, jobStatus) => {
    let status = jobStatus.replace("_", " ");

    if (
      engagementStatus &&
      ["rejected", "disengaged"].includes(engagementStatus)
    ) {
      status = engagementStatus;
    }

    return status;
  };

  const displayArrow = (
    engagementCategoryFilter,
    engagementId,
    jobId,
    jobTitle,
    cvLimit,
    applicationsCount,
    selectingCandidateActive,
    candidateId
  ) => {
    const onclickSelectCandidate = () => {
      trackEvent(
        "Submit Candidate",
        "Candidate Index Submit",
        "Candidate submitted from candidate index"
      );
    };

    if (
      isFeatureEnabled("displayPartnerEngagementsInYourJobs") &&
      engagementCategoryFilter === "pending" &&
      isPartnerUser
    ) {
      return (
        <Column flexSize={1} end={"end"}>
          <TextLink onClick={onAcceptClick}>Accept</TextLink>
        </Column>
      );
    }

    if (engagementCategoryFilter === "closed") {
      return <Column flexSize={1} />;
    }
    if (engagementCategoryFilter === "pending") {
      return (
        <Column flexSize={1} end={"end"}>
          <TextLink onClick={onDisengageClick}>Disengage</TextLink>
        </Column>
      );
    }
    // show 'Select' button if user is coming from 'Submit to Job' action
    if (
      selectingCandidateActive &&
      (cvLimit !== applicationsCount || isInfiniteCvLimit)
    ) {
      const alreadyApplied = engagement.job.applications.some(
        (app) => String(app.candidateId) === String(candidateId)
      );

      return (
        <Column flexSize={1}>
          <SelectButton
            disabled={alreadyApplied}
            href={
              !alreadyApplied &&
              `/recruiter/jobs/${jobId}/engagements/${engagementId}/applications/new?candidate_id=${candidateId}`
            }
            title="Select"
            onClick={onclickSelectCandidate}
          >
            {alreadyApplied ? "Applied" : "Select"}
          </SelectButton>
        </Column>
      );
    }
    // show empty Column if cv limit === applications count
    if (
      selectingCandidateActive &&
      cvLimit === applicationsCount &&
      !isInfiniteCvLimit
    ) {
      return (
        <Column flexSize={1}>
          <CvLimitFull>
            <FontAwesomeIcon icon={faTimesCircle} />
            <CvLimitFullText>cv limit full</CvLimitFullText>
          </CvLimitFull>
        </Column>
      );
    }
    // show chevron (default)
    return (
      <Column
        flexSize={1}
        data-test-id="engagement-link"
        center
        arrow
        offerMade={engagement.status === "offer_made"}
        pinkArrow={
          engagement.job.hasNewApplications || engagement.job.hasNewEngagements
        }
        onClick={() => handleLink(engagement.candidatesLink)}
      >
        <FontAwesomeIcon icon={faChevronRight} data-test-id="engagement-link" />
      </Column>
    );
  };

  return (
    <Wrapper style={style}>
      <Column isIE={isIE}>
        <Top titleField>
          <strong>
            <JobTitle>
              {jobTitleLink(engagement.status, engagement.job.status)}
            </JobTitle>
            {engagement.job.exclusive && (
              <Fragment>
                <Badge
                  data-tip={exclusiveJobTooltip}
                  data-for={`exclusive-job-tooltip-${engagement.id}`}
                >
                  Exclusive
                </Badge>
                <ReactTooltip
                  multiline
                  place="bottom"
                  offset={{ top: 180, left: 60 }}
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                  id={`exclusive-job-tooltip-${engagement.id}`}
                />
              </Fragment>
            )}
            {engagement.job.employer.accountType === "job_vendor" && (
              <Badge
                data-tip={percentageFeeTooltip}
                data-for={`split-fee-tooltip-${engagement.id}`}
              >
                Split Fee
              </Badge>
            )}
          </strong>
          {employerName(engagement.status, engagement.job.status)}
          <BadgeTooltip>
            <ReactTooltip
              multiline
              place="bottom"
              offset={{ top: 180, left: 60 }}
              type="dark"
              effect="solid"
              aria-haspopup="true"
              id={`split-fee-tooltip-${engagement.id}`}
            />
          </BadgeTooltip>
        </Top>
        <Bottom desktopStack location>
          <BottomItem noMobile>
            {
              formatRelative(Date.parse(engagement.job.listedAt), new Date(), {
                locale: enGB,
              }).split(" at")[0]
            }
          </BottomItem>
          {displayAgencyFeeMobile(engagementCategoryFilter, isPortalUser)}
          <Divider>|</Divider>
          <BottomItem>{engagement.job.location}</BottomItem>
        </Bottom>
      </Column>
      <Column flexSize={2} hideOnTablet isIE={isIE} ieWidth={isIE}>
        <Top />
        <Bottom desktopStack>
          <BottomItem>
            <strong>{currencySymbol} </strong>
            {engagement.job.salaryRange}
          </BottomItem>
          {displayAgencyFee(engagementCategoryFilter, isPortalUser)}
        </Bottom>
      </Column>
      <Column flexSize={2} capitalize hideOnTablet isIE={isIE} ieWidth={isIE}>
        <Top />
        <Bottom desktopStack>
          <BottomItem>{engagement.job.jobType}</BottomItem>
          <BottomItem offerMade={engagement.status === "offer_made"}>
            {displayedStatus(engagement.status, engagement.job.status)}
          </BottomItem>
        </Bottom>
      </Column>
      <Column flexSize={1} hideOnTablet isIE={isIE} ieWidth={isIE} center>
        <Top>
          {engagement.job.numberOfPositions > 1 ? (
            <NumberOfPositions>
              <FontAwesomeIcon icon={faThumbsUp} />
            </NumberOfPositions>
          ) : (
            <NumberOfPositionsPlaceholder />
          )}
          <FontAwesomeIcon icon={faFileAlt} />
          {engagementCategoryFilter !== "pending" && (
            <FontAwesomeIcon icon={faUsers} />
          )}
        </Top>
        <Bottom desktopStack numbers>
          <ReactTooltip
            html
            place="left"
            type="dark"
            effect="solid"
            aria-haspopup="true"
            id={`${engagement.id}-counts-tooltip`}
          />
          {engagement.job.numberOfPositions > 1 ? (
            <NumberOfPositions
              data-tip={multiplePostionsTooltip(
                engagement.job.filledPositionsCount,
                engagement.job.numberOfPositions
              )}
              data-test-id="number-of-positions-count"
              data-for={`${engagement.id}-counts-tooltip`}
            >
              {engagement.job.filledPositionsCount}/
              {engagement.job.numberOfPositions}
            </NumberOfPositions>
          ) : (
            <NumberOfPositionsPlaceholder />
          )}
          <Link
            href={
              engagementCategoryFilter !== "pending"
                ? engagement.candidatesLink
                : ""
            }
            data-test-id="engagements-link"
          >
            <BottomItem
              numbers
              data-html
              data-tip={cvLimitToolTip(cvLimitReached)}
              data-for={`${engagement.id}-counts-tooltip`}
            >
              <Number
                highlight={cvLimitReached}
                data-test-id="applications-submitted-count"
              >
                {engagement.applicationsCount}
                {isInfiniteCvLimit ? "" : `/${engagement.job.cvLimit}`}
              </Number>
            </BottomItem>
          </Link>
          {engagementCategoryFilter !== "pending" && (
            <Link href={engagement.candidatesLink}>
              <BottomItem
                numbers
                data-html
                data-tip={appsToolTip}
                data-for={`${engagement.id}-counts-tooltip`}
              >
                <Number highlight={engagement.userApplicationCountUnread > 0}>
                  {engagement.userApplicationCountUnread}
                </Number>
                <Number>{engagement.userApplicationCountRead}</Number>
              </BottomItem>
            </Link>
          )}
        </Bottom>
      </Column>
      {displayArrow(
        engagementCategoryFilter,
        engagement.id,
        engagement.jobId,
        engagement.job.title,
        engagement.job.cvLimit,
        engagement.applicationsCount,
        selectingCandidateActive,
        candidateId
      )}
    </Wrapper>
  );
};

Engagement.propTypes = {
  candidateId: PropTypes.string,
  engagementCategoryFilter: PropTypes.string,
  engagement: engagementType.isRequired,
  style: PropTypes.shape({}).isRequired,
  selectingCandidateActive: PropTypes.bool.isRequired,
  isPortalUser: PropTypes.bool.isRequired,
  isPartnerUser: PropTypes.bool.isRequired,
  onShowInfoClick: PropTypes.func,
  onDisengageClick: PropTypes.func,
};

Engagement.defaultProps = {
  engagementCategoryFilter: PropTypes.isNull,
  onShowInfoClick: () => {},
  onDisengageClick: () => {},
  showJobInfo: () => {},
  showDisengagementForm: () => {},
};
